/**
 * Office auth hook for ST_ONLY_MODE — PingFederate SSO via dialog, Famla JWT session.
 */
import { useState, useEffect, useCallback, useRef } from 'react';

import { openOfficeStAuthDialog } from './openOfficeStAuthDialog';
import {
    clearAuthSessionMarker,
    loadUserFromFamlaToken,
    markAuthSession,
    persistFamlaToken,
    readPersistedFamlaToken,
    type OfficeAuthMethod,
    type OfficeDerivedUser,
} from './officeFamlaSession';

const SILENT_REFRESH_MS = 45 * 60 * 1000;

export interface UseOfficeAuthReturn {
    isAuthenticated: boolean;
    userAzureId: string;
    userEmail: string;
    userName: string;
    accessToken: string | null;
    /** Default sign-in (ST SSO / email primary / Microsoft). */
    signIn: () => Promise<boolean>;
    signInWithEmail: () => Promise<boolean>;
    signInWithMicrosoft: () => Promise<boolean>;
    authMethod: OfficeAuthMethod;
    /** True when a usable Entra token is available (Microsoft Graph stakeholders). */
    hasEntraToken: boolean;
    isLoading: boolean;
    authError: string | null;
}

interface UseOfficeAuthStOptions {
    enabled: boolean;
}

export function useOfficeAuthSt({ enabled }: UseOfficeAuthStOptions): UseOfficeAuthReturn {
    const [accessToken, setAccessToken] = useState<string | null>(() =>
        enabled ? readPersistedFamlaToken() : null
    );
    const [isLoading, setIsLoading] = useState(enabled);
    const [authError, setAuthError] = useState<string | null>(null);
    const [derivedUser, setDerivedUser] = useState<OfficeDerivedUser>({
        userAzureId: '',
        userEmail: '',
        userName: 'User',
    });
    const refreshingRef = useRef(false);
    const accessTokenRef = useRef<string | null>(accessToken);
    accessTokenRef.current = accessToken;

    const applyFamlaToken = useCallback(async (token: string, refreshToken?: string) => {
        setAccessToken(token);
        setAuthError(null);
        persistFamlaToken(token, refreshToken);
        markAuthSession();
        const user = await loadUserFromFamlaToken(token);
        if (user) {
            setDerivedUser(user);
        }
    }, []);

    const restoreSession = useCallback(
        async (showLoading: boolean): Promise<boolean> => {
            if (!enabled) return false;
            if (refreshingRef.current) return Boolean(accessTokenRef.current);
            refreshingRef.current = true;
            if (showLoading) setIsLoading(true);

            try {
                const cached = readPersistedFamlaToken();
                if (cached) {
                    const user = await loadUserFromFamlaToken(cached);
                    if (user) {
                        setAccessToken(cached);
                        setDerivedUser(user);
                        markAuthSession();
                        return true;
                    }
                    clearAuthSessionMarker();
                    setAccessToken(null);
                }
                return false;
            } finally {
                refreshingRef.current = false;
                if (showLoading) setIsLoading(false);
            }
        },
        [enabled]
    );

    useEffect(() => {
        if (!enabled) return;
        void restoreSession(true);
    }, [enabled, restoreSession]);

    useEffect(() => {
        if (!enabled) return;

        const onVisible = () => {
            if (document.visibilityState === 'visible') {
                void restoreSession(false);
            }
        };
        const onFocus = () => {
            void restoreSession(false);
        };

        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        const intervalId = window.setInterval(() => {
            void restoreSession(false);
        }, SILENT_REFRESH_MS);

        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onFocus);
            window.clearInterval(intervalId);
        };
    }, [enabled, restoreSession]);

    const signIn = useCallback(async (): Promise<boolean> => {
        if (!enabled) return false;
        setIsLoading(true);
        setAuthError(null);
        try {
            const { accessToken: token, refreshToken } = await openOfficeStAuthDialog();
            await applyFamlaToken(token, refreshToken);
            setIsLoading(false);
            return true;
        } catch (error) {
            const message = error instanceof Error ? error.message : 'ST SSO sign-in failed';
            setAuthError(message);
            setAccessToken(null);
            clearAuthSessionMarker();
            setIsLoading(false);
            return false;
        }
    }, [applyFamlaToken, enabled]);

    const noopSignIn = useCallback(async () => false, []);

    if (!enabled) {
        return {
            isAuthenticated: false,
            userAzureId: '',
            userEmail: '',
            userName: 'User',
            accessToken: null,
            signIn: async () => false,
            signInWithEmail: async () => false,
            signInWithMicrosoft: async () => false,
            authMethod: 'none' as OfficeAuthMethod,
            hasEntraToken: false,
            isLoading: false,
            authError: null,
        };
    }

    return {
        isAuthenticated: Boolean(accessToken),
        userAzureId: derivedUser.userAzureId,
        userEmail: derivedUser.userEmail,
        userName: derivedUser.userName,
        accessToken,
        signIn,
        signInWithEmail: noopSignIn,
        signInWithMicrosoft: noopSignIn,
        authMethod: (accessToken ? 'password' : 'none') as OfficeAuthMethod,
        hasEntraToken: false,
        isLoading,
        authError,
    };
}
